import React, { useEffect, useRef, useState } from 'react';
import '../../../styles/components/landing/ClinicModal.css';
import '../../../styles/components/landing/DoctorDetailsModal.css';
import DoctorDetailsModal from './DoctorDetailsModal';

import defaultLogoIcon from '../../../assets/icons/landing/journal_icon.png';

const ClinicModal = ({ clinic, isOpen, onClose }) => {
  const modalRef = useRef(null);

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);

 // Handle view doctor details
const handleViewDoctorDetails = async (professional) => {
  try {
    // Create a complete doctor object from the professional data
    const fallbackDoctor = {
      ...professional,
      // Basic info
      firstName: professional.firstName || (professional.name ? professional.name.split(' ')[0] : ''),
      lastName: professional.lastName || (professional.name ? professional.name.split(' ').slice(1).join(' ') : ''),
      name: professional.name || `${professional.firstName || ''} ${professional.lastName || ''}`.trim(),
      email: professional.email || '',
      
      // Professional info
      title: professional.title || '',
      specialty: professional.specialty || (professional.specialties && professional.specialties.length > 0 ? professional.specialties[0] : ''),
      specialties: professional.specialties || [professional.specialty].filter(Boolean),
      verificationStatus: 'approved',
      
      // Contact info
      personalContactNumber: professional.personalContactNumber || professional.contactNumber || '',
      clinicContactNumber: professional.clinicContactNumber || '',
      clinicLocation: professional.clinicLocation || professional.location || '',
      clinicAddress: professional.clinicAddress || professional.address || '',
      
      // Service details
      consultationFee: professional.consultationFee || 0,
      telehealth: professional.telehealth || false,
      inPerson: professional.inPerson || false,
      
      // Professional background
      bio: professional.bio || professional.description || '',
      description: professional.description || professional.bio || '',
      experience: professional.experience || '',
      areasOfExpertise: professional.areasOfExpertise || '',
      yearsOfPractice: professional.yearsOfPractice || 0,
      
      // Credentials (ensure they're arrays)
      education: Array.isArray(professional.education) ? professional.education : [],
      licenses: Array.isArray(professional.licenses) ? professional.licenses : [],
      certifications: Array.isArray(professional.certifications) ? professional.certifications : [],
      licenseNumber: professional.licenseNumber || '',
      licenseIssuingAuthority: professional.licenseIssuingAuthority || '',
      
      // Profile picture
      profilePicture: professional.profilePicture || professional.photo || 'default-profile.png',
      photo: professional.photo || professional.profilePicture || 'default-profile.png',
      
      // Availability
      availability: professional.availability || null,
      
      // Set loading initially
      loading: true,
      
      // Flag to indicate this is fallback data
      isFallbackData: true
    };
    
    // Immediately show modal with data we have
    setSelectedDoctor(fallbackDoctor);
    setIsDoctorModalOpen(true);
    
    // Check if ID looks valid
    const isValidId = professional.id && /^[0-9a-fA-F]{24}$/.test(professional.id);
    
    if (isValidId) {
      try {
        // Try API call
        const response = await fetch(`/api/doctors/${professional.id}/details`);
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.success && result.data) {
            // Merge API data with fallback for any missing fields
            const mergedData = {
              ...fallbackDoctor,
              ...result.data,
              // Ensure these important display fields are set
              name: result.data.name || `${result.data.firstName || ''} ${result.data.lastName || ''}`.trim() || fallbackDoctor.name,
              profilePicture: result.data.profilePicture || result.data.photo || fallbackDoctor.profilePicture,
              photo: result.data.photo || result.data.profilePicture || fallbackDoctor.photo,
              loading: false,
              isFallbackData: false
            };
            setSelectedDoctor(mergedData);
          } else {
            // API returned error - use fallback data
            setSelectedDoctor({...fallbackDoctor, loading: false});
            console.warn('API returned error:', result.message);
          }
        } else {
          // API request failed - use fallback data
          setSelectedDoctor({...fallbackDoctor, loading: false});
          console.warn('API request failed with status:', response.status);
        }
      } catch (apiError) {
        // API exception - use fallback data
        console.error('API error:', apiError);
        setSelectedDoctor({...fallbackDoctor, loading: false});
      }
    } else {
      // Invalid ID - just use fallback data
      console.warn('Invalid doctor ID format or missing:', professional.id);
      setSelectedDoctor({...fallbackDoctor, loading: false});
    }
  } catch (error) {
    console.error('Error in handleViewDoctorDetails:', error);
    // Ensure we at least show something
    setSelectedDoctor({
      ...professional,
      name: professional.name || `${professional.firstName || ''} ${professional.lastName || ''}`.trim(),
      loading: false
    });
    setIsDoctorModalOpen(true);
  }
};

  // Handle close doctor modal
  const handleCloseDoctorModal = () => {
    setIsDoctorModalOpen(false);
    setSelectedDoctor(null);
    // Restore body overflow for clinic modal
    document.body.style.overflow = 'hidden';
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Close on ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (isDoctorModalOpen) {
          // Close doctor modal first
          setIsDoctorModalOpen(false);
          setSelectedDoctor(null);
        } else {
          // Close clinic modal
          onClose();
        }
      }
    };

    if (isOpen || isDoctorModalOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, isDoctorModalOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="clinic-modal-overlay">
        <div className="clinic-modal-container" ref={modalRef}>
          <button className="clinic-modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path 
                d="M18 6L6 18M6 6l12 12" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="clinic-modal-content">
            {/* Modal Header with Clinic Logo */}
            <div className="clinic-modal-header">
              <div className="clinic-modal-logo">
                <img 
                  src={clinic.logo} 
                  alt={`${clinic.name} logo`}
                  className="clinic-modal-logo-img"
                  onError={(e) => {
                    console.log('Logo failed to load:', e.target.src);
                    e.target.src = defaultLogoIcon;
                    e.target.onerror = null;
                  }}
                />
              </div>
              <div className="clinic-modal-header-content">
                <h2 className="clinic-modal-title">{clinic.name}</h2>
                <p className="clinic-modal-subtitle">{clinic.subtitle}</p>
              </div>
            </div>

            {/* Clinic Overview Section */}
            <section className="clinic-modal-section">
              <h3 className="clinic-modal-section-title">Clinic Overview</h3>
              <p className="clinic-modal-description">
                {clinic.fullDescription || clinic.description}
              </p>
            </section>

            {/* Services Section */}
            {clinic.services && clinic.services.length > 0 && (
              <section className="clinic-modal-section">
                <h3 className="clinic-modal-section-title">Services</h3>
                <div className="clinic-modal-services-grid">
                  {clinic.services.map((service, index) => (
                    <div key={index} className="clinic-modal-service-item">
                      <div className="clinic-modal-service-icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path 
                            d="M16.875 5.625L7.5 15L3.125 10.625" 
                            stroke="#4CAF50" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="clinic-modal-service-text">{service}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Mental Health Professionals - SINGLE SECTION ONLY */}
            {clinic.professionals && clinic.professionals.length > 0 && (
              <section className="clinic-modal-section">
                <h3 className="clinic-modal-section-title">Mental Health Professionals</h3>
                <div className="clinic-modal-professionals-grid">
                  {clinic.professionals.map((professional, index) => (
                    <div key={index} className="clinic-modal-professional-card">
                      {professional.photo && (
                        <div className="clinic-modal-professional-photo">
                          <img 
                            src={professional.photo} 
                            alt={`${professional.name}`}
                            className="clinic-modal-professional-img"
                          />
                        </div>
                      )}
                      <div className="clinic-modal-professional-info">
                        <h4 className="clinic-modal-professional-name">{professional.name}</h4>
                        {professional.title && (
                          <p className="clinic-modal-professional-title">{professional.title}</p>
                        )}
                        {professional.specialties && professional.specialties.length > 0 && (
                          <div className="clinic-modal-professional-specialties">
                            <span className="clinic-modal-professional-label">Specialties:</span>
                            <p className="clinic-modal-professional-value">
                              {professional.specialties.join(', ')}
                            </p>
                          </div>
                        )}
                        
                        {/* View Details Button */}
                        <button 
                          className="clinic-modal-professional-details-btn"
                          onClick={() => handleViewDoctorDetails(professional)}
                        >
                          <span>View Details</span>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path 
                              d="M6 12L10 8L6 4" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Clinic Information */}
            <section className="clinic-modal-section">
              <h3 className="clinic-modal-section-title">Clinic Information</h3>
              <div className="clinic-modal-info-grid">
                {clinic.address && (
                  <div className="clinic-modal-info-item">
                    <h4 className="clinic-modal-info-label">Address</h4>
                    <p className="clinic-modal-info-value">{clinic.address}</p>
                  </div>
                )}
                
                {clinic.yearEstablished && (
                  <div className="clinic-modal-info-item">
                    <h4 className="clinic-modal-info-label">Year Established</h4>
                    <p className="clinic-modal-info-value">{clinic.yearEstablished}</p>
                  </div>
                )}
                
                {clinic.appointmentTypes && clinic.appointmentTypes.length > 0 && (
                  <div className="clinic-modal-info-item">
                    <h4 className="clinic-modal-info-label">Appointment Types</h4>
                    <div className="clinic-modal-appointment-types">
                      {clinic.appointmentTypes.map((type, index) => (
                        <span key={index} className="clinic-modal-appointment-type">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Contact Information */}
            <section className="clinic-modal-section">
              <h3 className="clinic-modal-section-title">Contact Information</h3>
              <div className="clinic-modal-contact-grid">
                {clinic.phone && (
                  <div className="clinic-modal-contact-item">
                    <div className="clinic-modal-contact-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path 
                          d="M18.3334 14.1V16.6C18.3344 16.8321 18.2868 17.0618 18.1939 17.2744C18.1009 17.4871 17.9644 17.678 17.7934 17.8348C17.6224 17.9916 17.4203 18.1113 17.2005 18.186C16.9806 18.2607 16.7477 18.2893 16.5167 18.27C13.9523 17.9627 11.4892 17.0084 9.32508 15.4917C7.31147 14.0964 5.60765 12.3926 4.21242 10.379C2.69167 8.20977 1.73736 5.73925 1.43341 3.16833C1.41409 2.93742 1.44255 2.70465 1.51686 2.48488C1.59117 2.26512 1.71035 2.06327 1.8667 1.89218C2.02305 1.72109 2.21355 1.58438 2.42577 1.49089C2.63799 1.3974 2.86734 1.34885 3.09908 1.34833H5.59908C6.00487 1.34435 6.39772 1.48709 6.70179 1.75132C7.00586 2.01556 7.19799 2.38351 7.24241 2.78C7.3301 3.51005 7.49957 4.22895 7.74908 4.92C7.84566 5.17563 7.86887 5.45412 7.81572 5.7225C7.76257 5.99087 7.63557 6.23727 7.44908 6.43L6.39908 7.48C7.70127 9.5607 9.44004 11.2995 11.5207 12.6017L12.5707 11.5517C12.7635 11.3652 13.0099 11.2382 13.2782 11.185C13.5466 11.1319 13.8251 11.1551 14.0807 11.2517C14.7718 11.5012 15.4907 11.6707 16.2207 11.7583C16.6216 11.8032 16.9929 11.9992 17.2576 12.308C17.5222 12.6168 17.6619 13.0151 17.6524 13.4217L18.3334 14.1Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="clinic-modal-contact-content">
                      <h4 className="clinic-modal-contact-label">Phone</h4>
                      <p className="clinic-modal-contact-value">
                        <a href={`tel:${clinic.phone}`} className="clinic-modal-contact-link">
                          {clinic.phone}
                        </a>
                      </p>
                    </div>
                  </div>
                )}
                
                {clinic.email && (
                  <div className="clinic-modal-contact-item">
                    <div className="clinic-modal-contact-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path 
                          d="M3.33341 3.33333H16.6667C17.5834 3.33333 18.3334 4.08333 18.3334 5V15C18.3334 15.9167 17.5834 16.6667 16.6667 16.6667H3.33341C2.41675 16.6667 1.66675 15.9167 1.66675 15V5C1.66675 4.08333 2.41675 3.33333 3.33341 3.33333Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                        <path 
                          d="M18.3334 5L10.0001 10.8333L1.66675 5" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="clinic-modal-contact-content">
                      <h4 className="clinic-modal-contact-label">Email</h4>
                      <p className="clinic-modal-contact-value">
                        <a href={`mailto:${clinic.email}`} className="clinic-modal-contact-link">
                          {clinic.email}
                        </a>
                      </p>
                    </div>
                  </div>
                )}
                
                {clinic.website && (
                  <div className="clinic-modal-contact-item">
                    <div className="clinic-modal-contact-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path 
                          d="M10.0001 18.3333C14.6025 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6025 1.66667 10.0001 1.66667C5.39771 1.66667 1.66675 5.39763 1.66675 10C1.66675 14.6024 5.39771 18.3333 10.0001 18.3333Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                        <path 
                          d="M1.66675 10H18.3334" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                        <path 
                          d="M10.0001 1.66667C12.0845 3.94863 13.269 6.91003 13.3334 10C13.269 13.09 12.0845 16.0514 10.0001 18.3333C7.91568 16.0514 6.73112 13.09 6.66675 10C6.73112 6.91003 7.91568 3.94863 10.0001 1.66667V1.66667Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="clinic-modal-contact-content">
                      <h4 className="clinic-modal-contact-label">Website</h4>
                      <p className="clinic-modal-contact-value">
                        <a 
                          href={clinic.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="clinic-modal-contact-link"
                        >
                          {clinic.website}
                        </a>
                      </p>
                    </div>
                  </div>
                )}
                
                {clinic.address && (
                  <div className="clinic-modal-contact-item">
                    <div className="clinic-modal-contact-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path 
                          d="M10.0001 10.8333C11.3808 10.8333 12.5001 9.71404 12.5001 8.33333C12.5001 6.95262 11.3808 5.83333 10.0001 5.83333C8.61937 5.83333 7.50008 6.95262 7.50008 8.33333C7.50008 9.71404 8.61937 10.8333 10.0001 10.8333Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                        <path 
                          d="M16.6666 8.33333C16.6666 14.1667 10.0001 18.3333 10.0001 18.3333C10.0001 18.3333 3.33331 14.1667 3.33331 8.33333C3.33331 6.56521 4.03569 4.86953 5.28593 3.61929C6.53618 2.36904 8.23186 1.66667 9.99998 1.66667C11.7681 1.66667 13.4638 2.36904 14.714 3.61929C15.9643 4.86953 16.6666 6.56521 16.6666 8.33333V8.33333Z" 
                          stroke="#548170" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="clinic-modal-contact-content">
                      <h4 className="clinic-modal-contact-label">Address</h4>
                      <p className="clinic-modal-contact-value">
                        {clinic.address}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* CTA Button at the bottom */}
            <div className="clinic-modal-cta">
              <button className="clinic-modal-cta-button">
                Book an Appointment
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path 
                    d="M3.33325 10H16.6666" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M10.8333 4.16667L16.6666 10L10.8333 15.8333" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Doctor Details Modal */}
      <DoctorDetailsModal 
        doctor={selectedDoctor}
        isOpen={isDoctorModalOpen}
        onClose={handleCloseDoctorModal}
      />
    </>
  );
};

export default ClinicModal;